import React from "react";
import { MEETUP_STATUS_NAMES, tradingName } from "./data.jsx";
import { useApp, useMedia } from "./store.jsx";
import { SectionHead, MessagesSkeleton, ConversationRowsSkeleton } from "./chrome.jsx";
import { TradeActions } from "./trades.jsx";
import { ComposerListingPreview, MessageLine } from "./listing-link-card.jsx";
import { firstListingIn } from "./listing-links.js";

const { Button, IconButton, Icon, Badge, Card, Input, TraderRow, EmptyState, MeetupCard, FluteDivider } = window.CardboardmarketDesignSystem_2a3239;

function Thread({ thread, onBack, full }) {
  const { sendMessage, openThread, trades, actingAsShop, go , t } = useApp();
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const bottom = React.useRef(null);
  const scroller = React.useRef(null);

  const messages = openThread.id === thread.id ? openThread.messages : [];
  // The cards for the listing links in it, keyed by listing id. See listing-link-card.jsx.
  const cards = openThread.id === thread.id ? openThread.cards : undefined;

  /*
   * A listing link in the draft is previewed above the box, and its × sends
   * that message as the link alone. The choice is remembered against the
   * LISTING it was made for, so pasting a different link asks again.
   */
  const draftListing = firstListingIn(draft);
  const [linkOnlyFor, setLinkOnlyFor] = React.useState(null);
  const linkOnly = !!draftListing && linkOnlyFor === draftListing;

  /**
   * Messages and EVERY trade with this person, in the order they happened.
   *
   * The meetup card used to be appended after every message however old the
   * proposal was, so one from Tuesday sat below a reply from this morning and the
   * conversation read backwards. It is one of the things that happened here, so it
   * belongs where it happened.
   *
   * ALL OF THEM, not one. This was `.find()`, which answers with the first match
   * and silently drops the rest — so asking the same seller for three cards put
   * one proposal in the conversation and left two with nowhere to appear at all.
   * A trade opens a conversation with the person it is addressed to, and asking
   * twice does not open a second thread, so the thread is the only place several
   * of them can be seen together. Nothing here is a redesign: the timeline
   * already interleaves by timestamp, and each proposal simply takes its own
   * place in it.
   *
   * EVERY STATE, which was the open question and has now been answered: "it
   * should be all trades, independently of their state". So the filter is the
   * person and nothing else — proposed, accepted, declined, cancelled and
   * completed all appear, each where it happened.
   *
   * A conversation is the record of what the two of you did, and a trade that was
   * declined in March is part of that record; leaving it out made the thread
   * disagree with the Trades screen, which has shown terminal trades under "Past"
   * all along. What this needed was not the filter — that is one line — but
   * buttons that do not assume a live trade. They come from `can` now, which the
   * server computes, so a finished or abandoned trade simply offers nothing. See
   * TradeActions in trades.jsx.
   *
   * A trade with no readable timestamp goes last rather than to 1970 — unknown is
   * not "long ago", and burying a live proposal at the top of the thread would be
   * a worse answer than leaving it where it used to be.
   *
   * THE TIE IS BROKEN EXPLICITLY, and with several trades it now happens. Two
   * unreadable timestamps are both `Infinity`, and `a.at - b.at` on those is NaN
   * — a comparator answering NaN leaves the order to the sort rather than
   * deciding it, which is the definition of arbitrary even where it happens to
   * come out stable. `order` is the position each entry went in at, so equal
   * instants keep the order they arrived in: the messages as the server sent
   * them, then the trades as the server sent them.
   */
  const timeline = React.useMemo(() => {
    const entries = messages.map((m, i) => ({ kind: 'message', at: m.atMs || 0, order: i, item: m }));
    trades
      .filter((t) => t.them.id === thread.with.id)
      .forEach((t, i) => {
        const at = Date.parse(t.createdAt);
        entries.push({
          kind: 'trade',
          at: Number.isFinite(at) ? at : Infinity,
          order: messages.length + i,
          item: t,
        });
      });
    return entries.sort((a, b) => (a.at === b.at ? a.order - b.order : a.at - b.at));
  // The filter lives INSIDE the memo on purpose. Pulled out, it would build a new
  // array every render and this would recompute every render — a memo that
  // memoises nothing.
  }, [messages, trades, thread.with.id]);

  /*
   * Opened at the NEWEST message, and kept there as more arrive.
   *
   * A conversation opened at the top is a conversation opened at whatever was
   * said first, which for anything longer than a screen means scrolling before
   * you can read the thing you came for.
   *
   * The scroll container is moved directly rather than by `scrollIntoView`,
   * which walks up the tree and scrolls every ancestor it can — on the phone
   * layout that moved the PAGE instead of the list, which is the behaviour this
   * is replacing.
   */
  React.useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, timeline.length]);

  /*
   * The composer, so the caret can be put back after a send.
   *
   * A ref on the wrapper rather than on `Input`: it is a design-system component
   * and does not forward one, and reaching for the element inside is honest
   * about that rather than adding a prop to the library for this.
   */
  const composer = React.useRef(null);
  const keepTyping = () => {
    const box = composer.current && composer.current.querySelector('input');
    if (box) box.focus();
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    /*
     * FOCUS FIRST, BEFORE THE AWAIT.
     *
     * Tapping Send moves focus to the button and closes the keyboard, and the
     * next thing most people do is type again — so the keyboard has to reopen,
     * which browsers only allow while a user gesture is still being handled.
     * Calling this after the round trip is too late: the gesture is over, the
     * call is ignored, and the fix appears to work on a desktop and does nothing
     * on the device it was asked for.
     */
    keepTyping();
    setBusy(true);
    // Cleared optimistically: retyping a message because the network was slow is
    // worse than the small chance of having to retype after a real failure.
    setDraft('');
    try {
      await sendMessage(thread.with.id, text, { noCard: linkOnly });
      setLinkOnlyFor(null);
    } catch (e) {
      setDraft(text);
    } finally {
      setBusy(false);
      // Again afterwards: the button's `disabled` flips while the request is in
      // flight, and a disabled element that had focus hands it back to the body.
      keepTyping();
    }
  };
  return (
    /*
     * `full` is the phone case, where the conversation IS the screen; the pane
     * is the wide one, beside the list.
     *
     * Both are bounded by the window and scroll only their messages, so who you
     * are talking to stays at the top and the box to type in stays at the
     * bottom. They get there differently, because a pane cannot be pinned to the
     * viewport's edges without stranding the inbox next to it — `.cbm-thread` is
     * anchored between the header and the bottom bar, `.cbm-thread--pane` fills
     * a shell that is one viewport tall. See app.css for both.
     *
     * The pane used to be `min-height:520px` and no maximum, which is a floor
     * with nothing above it: enough messages and the card grew past the window
     * and the whole page scrolled, taking the header and the composer with it.
     *
     * The two structural classes are unconditional now. They were the phone's
     * only, so on a wide screen the scroller was not a scroller: the effect
     * below set `scrollTop` on an element with nothing to scroll, which is a
     * no-op, and a desktop conversation opened at its oldest message.
     */
    <Card padding="0" className={full ? 'cbm-thread' : 'cbm-thread--pane'}>
      <div className="cbm-thread__fixed" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', padding: 'var(--space-5)', borderBottom: '1px solid var(--border-hairline)' }}>
        {onBack && <IconButton icon="arrow-left" label={t("Back to inbox")} variant="ghost" size="sm" onClick={onBack} />}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ font: 'var(--type-h3)', color: 'var(--text-strong)' }}>{tradingName(thread.with)}</span>
            {/*
              SOMEBODY FROM CARDBOARDMARKET, said where the message is read.

              Asked for, and the reason is a real one: an operator now writes to
              winners to arrange a prize, and "what is your address?" from a
              stranger is indistinguishable from the oldest trick there is. The
              badge names US rather than a role — "admin" tells a trader nothing
              about whether to trust the message, and it is the platform they
              are being asked to trust.

              Only ever in a conversation. There is no badge on a listing, a
              profile or a trade: who runs the place is not a fact anybody needs
              while they are buying a card.
            */}
            {thread.with && thread.with.staff ? (
              <Badge tone="verified" icon="check">{t("Cardboardmarket")}</Badge>
            ) : null}
          </div>
        </div>
        <IconButton icon="user" label={t("View profile")} variant="ghost" size="sm" />
      </div>
      <div
        ref={scroller}
        className="cbm-thread__scroll"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)', padding: 'var(--space-6)', background: 'var(--bg-page)' }}
      >
        {/*
          The conversation's shape while it is on its way, rather than the word
          "Loading…" standing where a chat is about to be. Same reasoning as the
          browse grid: the request takes the same time either way, and what gets
          reported as slowness is the jump.
        */}
        {openThread.loading && !messages.length ? (
          <MessagesSkeleton label={t("Loading the conversation.")} />
        ) : null}
        {/*
          Messages and the trade, in the order they happened.
          The meetup card used to sit after every message however old it was, so a
          proposal from Tuesday appeared below a reply from this morning and the
          conversation read backwards. It is one of the things that happened in the
          conversation, so it belongs where it happened.
        */}
        {timeline.map((entry) => (entry.kind === 'message' ? (
          // The bubble, and a card under it for each listing link it carries.
          <MessageLine key={entry.item.id} message={entry.item} cards={cards} wide={!full} />
        ) : (
          <div key={'trade-' + entry.item.id} style={{ margin: '4px 0' }}>
            <MeetupCard
              place={entry.item.place || t("Not agreed yet")}
              address={entry.item.address}
              when={entry.item.when
                || (entry.item.atCounter ? t("While the shop is open") : t("Time not agreed yet"))}
              withWhom={entry.item.withWhom}
              items={entry.item.items}
              status={entry.item.cardStatus}
              // The badge's tone is the design system's; its words are ours. The
              // fallback mirrors MeetupCard's own `STATE[status] || STATE.proposed`,
              // so an unexpected state cannot leave the tone and the label
              // disagreeing about what it says.
              statusLabel={t(MEETUP_STATUS_NAMES[entry.item.cardStatus] || MEETUP_STATUS_NAMES.proposed)}
              // Accept HERE, not on another screen. "Review" used to navigate to
              // /trades?tab=proposed — a list you then have to search for the
              // trade you were just talking about. A meetup is agreed in the
              // conversation because the conversation is where it gets agreed.
              //
              // No `onMessage`: you are already in the conversation, and a button
              // that takes you where you are is noise. The Trades screen passes
              // one because it has somewhere to send you.
              action={<TradeActions trade={entry.item} inRequests={actingAsShop} />}
            />
          </div>
        )))}
        <div ref={bottom} />
      </div>
      <div ref={composer} className="cbm-thread__fixed" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', padding: 'var(--space-5)', borderTop: '1px solid var(--border-hairline)', background: 'var(--white)' }}>
        {draftListing && !linkOnly ? (
          <ComposerListingPreview listingId={draftListing} onDismiss={() => { setLinkOnlyFor(draftListing); keepTyping(); }} />
        ) : null}
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <Input wrapperStyle={{ flex: 1 }} placeholder={t("Suggest a place and a time")} value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }} />
          <Button iconRight="send" onClick={send} disabled={!draft.trim() || busy}>{t("Send")}</Button>
        </div>
      </div>
    </Card>
  );
}

export function InboxScreen() {
  const { route, threads, threadsState, go, navigate, readThread, pending, clearPending, t } = useApp();
  const compact = useMedia('(max-width:1000px)');

  /**
   * Which conversation is open lives in the URL, like everything else — so a
   * conversation can be linked to, and back leaves it rather than the app.
   */
  const openId = route.id || (compact ? null : threads[0] && threads[0].id);
  // A conversation that does not exist yet is still a conversation to look at:
  // the composer works, and sending the first message creates it.
  /*
   * A conversation with nobody's messages in it yet is open only while the URL
   * SAYS so — `?to=<their id>` — not merely because `pending` is still in state.
   *
   * It used to be the state alone, and that is what made back useless here: the
   * address changed and the screen did not, so the button appeared dead. Reading
   * it from the route puts this case on the same footing as a real thread, where
   * back has always worked, and needs no history handling of its own.
   *
   * `pending` still carries the NAME, because a name in a URL would be untrusted
   * text rendered as a person. On a reload the id survives and the name does
   * not, so the screen falls back to the list — which is what the inbox has
   * always promised a reload does.
   */
  /*
   * And for the moment after its first message, at its new `?id=` but before
   * the conversation list has been read again with it in — `sendMessage` sets
   * `pending.conversationId` for exactly that gap.
   */
  const justStarted = pending && pending.conversationId && route.id === pending.conversationId;
  const open = pending && (route.to === pending.id || justStarted)
    ? { id: pending.conversationId || null, with: pending, unread: 0, preview: '', at: '' }
    : threads.find((t) => t.id === openId);

  React.useEffect(() => { if (openId && !pending) readThread(openId); }, [openId, pending, readThread]);

  const pick = (id) => navigate('inbox', { id });

  /*
   * WHILE A CONVERSATION FILLS THE SCREEN, BACK BELONGS TO THE CONVERSATION.
   *
   * The thread is a view of the inbox — `/inbox?id=…` — so the browser's back
   * button returns to whatever the previous ENTRY was, and that is only the
   * conversation list if you arrived from it. Open a thread from a push
   * notification, or from a seller's "Message" button, and back drops you on a
   * listing or leaves the app entirely, which is not what a back arrow means on
   * a screen that is full of one conversation.
   *
   * So on a phone, back closes the conversation and shows the list, however you
   * got here. `replace`, because the entry it lands on has already been popped —
   * pushing would leave a dead step that does nothing but be pressed twice.
   *
   * Only in `compact`. On a wide screen the list is ALREADY on screen beside the
   * thread, so back has nothing to close and should do the ordinary thing.
   */
  /*
   * A LIST TO GO BACK TO, when the conversation is where the session started.
   *
   * `popstate` cannot help here: arriving from a notification is a fresh
   * document, so back is a cross-document navigation and no handler of ours
   * ever runs. What works is giving the browser somewhere to go — rewrite this
   * entry as the list and push the conversation on top of it, so the history a
   * cold start produces is the history the same journey would have had inside
   * the app.
   *
   * Once per document, guarded by the ref: doing it on every render would push
   * an entry per keystroke.
   */
  const seeded = React.useRef(false);
  /*
   * Where we were before this render, so "did they come from the list" can be
   * answered. `route.name` alone cannot: by the time the conversation is on
   * screen it IS the inbox route, thread and list alike.
   */
  const cameFrom = React.useRef(null);
  React.useEffect(() => {
    const was = cameFrom.current;
    cameFrom.current = { name: route.name, id: route.id ?? null };
    if (!(compact && open) || seeded.current) return;

    /*
     * The list was already behind us if we opened this FROM the list — which is
     * the one journey that always worked. Everything else needs one put there:
     * a cold start on a notification, a shared link, a seller's "Message"
     * button. The first version of this only covered the cold start and left
     * every in-app arrival still landing wherever the person happened to be.
     */
    const fromTheList = was && was.name === 'inbox' && !was.id;
    if (fromTheList) { seeded.current = true; return; }

    seeded.current = true;
    /*
     * A `pending` conversation — one with nobody's messages in it yet — renders
     * at `/inbox`, the LIST's own URL, because `messageTrader` holds the
     * recipient in state rather than in the address. So `here` is `/inbox` and
     * this pushes a second entry with the same URL.
     *
     * That looks pointless and is the entire mechanism: back then consumes an
     * entry without changing the address, `onPop` puts the person down, and the
     * list is what is left. Without it, back leaves `/inbox` for whatever was
     * before — the listing you pressed Message on — which is the bug.
     */
    const here = window.location.pathname + window.location.search;
    window.history.replaceState({}, '', '/inbox');
    window.history.pushState({}, '', here);
  }, [compact, open, route.name, route.id]);

  React.useEffect(() => {
    if (!(compact && open)) return undefined;
    const onPop = () => {
      // A conversation with nobody in it yet is held in state, not in the URL, so
      // it survives the navigation that was meant to leave it. Put the person
      // down first, then make sure we are looking at the list.
      if (pending) clearPending();
      const stillAThread = window.location.pathname.startsWith('/inbox') && window.location.search.includes('id=');
      if (!stillAThread && window.location.pathname !== '/inbox') navigate('inbox', {}, { replace: true });
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [compact, open, navigate, pending, clearPending]);

  /*
   * On a phone the conversation is the whole screen, so it gets the viewport.
   *
   * The separate "Inbox" back link above it is gone: the thread header already
   * carries one, and two stacked back affordances cost a row of vertical space
   * on the screen that has least of it while saying the same thing twice.
   */
  if (compact && open) return <Thread thread={open} onBack={() => navigate('inbox')} full />;

  return (
    <>
      <SectionHead title={t("Inbox")} sub={t("Agree a place and a time. Everything else happens face to face.")} />
      <div className="cbm-split">
        <div className="cbm-stack" style={{ gap: 0 }}>
          {threads.length ? threads.map((th) => (
            <div key={th.id} style={{ borderBottom: '1px solid var(--border-hairline)', background: th.id === openId && !compact ? 'var(--surface-sunken)' : 'transparent' }}>
              <TraderRow
                name={tradingName(th.with)}
                // "You: ..." so a list of previews says who spoke without anyone
                // having to open each one to find outh.
                meta={th.lastFromYou ? `${t("You:")} ${th.preview}` : th.preview}
                onClick={() => pick(th.id)}
                /*
                  THE BADGE IN THE LIST TOO, but only where the row has the
                  width for it.

                  In `action` rather than a slot of its own: `TraderRow` has no
                  badge slot, and a prop it does not know would be forwarded
                  onto the DOM and silently dropped — which is what the first
                  version of this did.

                  AND NOT IN THE TWO-PANE LAYOUT, which was measured rather
                  than guessed: at 1280 the badge squeezed "Asier P" down to
                  "Asie…", and a truncated NAME is a worse trade than a missing
                  badge in a row whose whole job is saying who wrote. The
                  desktop layout shows the open thread beside the list anyway,
                  and the header there carries it. On a phone the row is full
                  width, the name fits, and the preview gives up the space.
                */
                action={
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {compact && th.with && th.with.staff
                      ? <Badge tone="verified" icon="check">{t("Cardboardmarket")}</Badge>
                      : null}
                    {th.unread
                      ? <Badge tone="brand">{th.unread}</Badge>
                      : <span style={{ font: 'var(--type-meta)', color: 'var(--text-faint)' }}>{th.at}</span>}
                  </span>
                }
              />
            </div>
          ))
            /*
             * A failure is not an empty inbox, and neither is a question still in
             * flight. Both branches were missing: this list ran straight to "No
             * messages yet" with a button to go and browse, over a request that
             * had not come back, on the screen somebody opens specifically to
             * find out whether anybody replied.
             *
             * The list has to be empty as well as loading. The poll refetches the
             * conversations every four seconds while this screen is open, and
             * replacing a list somebody is reading with grey rows every four
             * seconds would be a worse bug than the one being fixed.
             */
            : threadsState === 'error' ? (
              <EmptyState icon="alert-triangle" title={t("We could not load your conversations")}
                description={t("Something went wrong at our end, not yours. Nothing has been lost.")} />
            ) : threadsState === 'loading' ? (
              <ConversationRowsSkeleton label={t("Loading your conversations.")} />
            ) : <EmptyState icon="message-circle" title={t("No messages yet")} description={t("Propose a trade and the conversation starts here.")} action={<Button onClick={() => go('browse')}>{t("Browse nearby cards")}</Button>} />}
        </div>
        {!compact && open ? <Thread thread={open} /> : null}
      </div>
    </>
  );
}

